package com.vidaensupalabra.vsp.datos

import io.ktor.client.HttpClient
import io.ktor.client.engine.js.Js
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.CancellationException
import kotlin.js.Date

object YouTubeRepository {
    private val client = HttpClient(Js) {
        expectSuccess = false // Previene excepciones costosas por códigos 4xx/5xx
    }
    
    private const val CACHE_EXPIRATION_MS = 1000 * 60 * 60 // 1 hora de caché en memoria

    private var cachedLatestVideoId: String? = null
    private var lastVideoFetchTime: Double = 0.0

    private var cachedPlaylistItems: List<PlaylistItem>? = null
    private var lastPlaylistFetchTime: Double = 0.0

    /**
     * Ejecuta peticiones usando múltiples proxies de respaldo para garantizar
     * alta disponibilidad cuando un servicio gratuito se cae en las noches.
     */
    private suspend fun fetchFeedWithFallbacks(targetUrl: String): String? {
        val encodedUrl = js("encodeURIComponent(targetUrl)") as String

        // Mapeo de proxies junto a un flag que determina si necesitan la URL codificada en su totalidad
        val proxies = listOf(
            "https://api.rss2json.com/v1/api.json?rss_url=" to true, // Especializado en RSS, rara vez bloqueado
            "https://corsproxy.io/?" to false,
            "https://api.codetabs.com/v1/proxy/?quest=" to true,
            "https://api.allorigins.win/raw?url=" to true
        )

        for ((proxyUrl, requiresEncode) in proxies) {
            try {
                val urlToFetch = if (requiresEncode) "$proxyUrl$encodedUrl" else "$proxyUrl$targetUrl"
                val response: HttpResponse = client.get(urlToFetch) {
                    header("Accept", "application/xml, text/xml, */*")
                }

                if (response.status.value in 200..299) {
                    val responseBody = response.bodyAsText()
                    // Validar si es XML nativo
                    if (responseBody.contains("<feed") || responseBody.contains("<rss")) {
                        return responseBody
                    }
                    // Validar si es la respuesta JSON convertida por rss2json
                    if (responseBody.trimStart().startsWith("{") && responseBody.contains("\"items\"")) {
                        return responseBody
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                // Fallo silencioso del proxy, se intentará el siguiente en la lista para no hacer log spam.
            }
        }
        
        println("WARN YouTubeRepository: Todos los proxies fallaron para $targetUrl")
        return null
    }

    /**
     * Fallback de Web Scraping. Si el RSS de YouTube es bloqueado o devuelve 404,
     * extrae el código fuente HTML público de la playlist para parsearlo.
     */
    private suspend fun fetchHtmlWithFallbacks(targetUrl: String): String? {
        val encodedUrl = js("encodeURIComponent(targetUrl)") as String
        val proxies = listOf(
            "https://corsproxy.io/?" to false,
            "https://api.allorigins.win/raw?url=" to true,
            "https://api.codetabs.com/v1/proxy/?quest=" to true
        )

        for ((proxyUrl, requiresEncode) in proxies) {
            try {
                val urlToFetch = if (requiresEncode) "$proxyUrl$encodedUrl" else "$proxyUrl$targetUrl"
                val response: HttpResponse = client.get(urlToFetch)
                if (response.status.value in 200..299) {
                    val responseBody = response.bodyAsText()
                    if (responseBody.contains("ytInitialData")) return responseBody
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                // Fallo silencioso, intentar siguiente proxy
            }
        }
        return null
    }

    suspend fun getLatestVideoId(channelId: String): String? {
        val now = Date.now()
        if (cachedLatestVideoId != null && now - lastVideoFetchTime < CACHE_EXPIRATION_MS) {
            return cachedLatestVideoId
        }

        return try {
            val targetUrl = "https://www.youtube.com/feeds/videos.xml?channel_id=$channelId"
            val response = fetchFeedWithFallbacks(targetUrl)
            if (response == null) {
                return cachedLatestVideoId
            }

            val isJson = response.trimStart().startsWith("{")
            
            val id = if (isJson) {
                val regex = """"guid"\s*:\s*"yt:video:([^"]+)"""".toRegex(RegexOption.IGNORE_CASE)
                regex.find(response)?.groups?.get(1)?.value
            } else {
                val regex = """<yt:videoId[^>]*>([^<]+)</yt:videoId>""".toRegex(RegexOption.IGNORE_CASE)
                regex.find(response)?.groups?.get(1)?.value
            }
            
            if (id != null) {
                cachedLatestVideoId = id
                lastVideoFetchTime = now
            }
            cachedLatestVideoId
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("ERROR YouTubeRepository: Excepción crítica en getLatestVideoId - Error: ${e.message}")
            cachedLatestVideoId
        }
    }

    suspend fun getPlaylistItems(playlistId: String): List<PlaylistItem>? {
        val now = Date.now()
        if (cachedPlaylistItems != null && now - lastPlaylistFetchTime < CACHE_EXPIRATION_MS) {
            return cachedPlaylistItems
        }

        return try {
            // Utilizamos el feed RSS oficial de YouTube para Playlists en lugar de APIs frágiles
            val targetUrl = "https://www.youtube.com/feeds/videos.xml?playlist_id=$playlistId"
            val response = fetchFeedWithFallbacks(targetUrl)

            val items = mutableListOf<PlaylistItem>()
            if (response != null) {
                val isJson = response.trimStart().startsWith("{")
                if (isJson) {
                    val itemRegex = """"title"\s*:\s*"((?:\\.|[^"\\])*)"[^}]*?"guid"\s*:\s*"yt:video:([^"]+)"""".toRegex(RegexOption.IGNORE_CASE)
                    itemRegex.findAll(response).forEach { matchResult ->
                        val rawTitle = matchResult.groupValues[1]
                        val videoId = matchResult.groupValues[2]
                        val title = rawTitle.replace("\\u0026", "&").replace("\\\"", "\"").replace("\\/", "/")
                        items.add(PlaylistItem(videoId, title))
                    }
                } else {
                    val entryRegex = """<entry>([\s\S]*?)</entry>""".toRegex(RegexOption.IGNORE_CASE)
                    val videoIdRegex = """<yt:videoId>([^<]+)</yt:videoId>""".toRegex(RegexOption.IGNORE_CASE)
                    val titleRegex = """<title>([^<]+)</title>""".toRegex(RegexOption.IGNORE_CASE)
                    entryRegex.findAll(response).forEach { matchResult ->
                        val entryHtml = matchResult.groupValues[1]
                        val videoId = videoIdRegex.find(entryHtml)?.groupValues?.get(1)
                        val title = titleRegex.find(entryHtml)?.groupValues?.get(1)
                        if (videoId != null && title != null) {
                            items.add(PlaylistItem(videoId, title))
                        }
                    }
                }
            }

            // Fallback robusto: Web Scraping HTML si el feed RSS falló, está vacío o la playlist está bloqueada
            if (items.isEmpty()) {
                println("WARN YouTubeRepository: RSS falló para playlist $playlistId. Intentando Web Scraping HTML...")
                val htmlUrl = "https://www.youtube.com/playlist?list=$playlistId"
                val htmlResponse = fetchHtmlWithFallbacks(htmlUrl)
                
                if (htmlResponse != null) {
                    val regex = """"playlistVideoRenderer":\{"videoId":"([^"]{11})".*?"title":\{"runs":\[\{"text":"((?:\\.|[^"\\])+?)"\}\]""".toRegex(RegexOption.IGNORE_CASE)
                    regex.findAll(htmlResponse).forEach { match ->
                        val videoId = match.groupValues[1]
                        val title = match.groupValues[2].replace("\\u0026", "&").replace("\\\"", "\"").replace("\\/", "/")
                        if (!title.contains("Private video", true) && !title.contains("Deleted video", true)) {
                            items.add(PlaylistItem(videoId, title))
                        }
                    }
                }
            }

            val fetchedItems = items.distinctBy { it.videoId }

            if (fetchedItems.isNotEmpty()) {
                cachedPlaylistItems = fetchedItems
                lastPlaylistFetchTime = now
                return fetchedItems
            }
            
            println("WARN YouTubeRepository: RSS no contenía videos para la playlist $playlistId")
            return cachedPlaylistItems ?: emptyList()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("ERROR YouTubeRepository: Excepción crítica en getPlaylistItems - Error: ${e.message}")
            cachedPlaylistItems ?: emptyList()
        }
    }
}